import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/errors';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { KeyRound, Loader2, LogOut } from 'lucide-react';

/**
 * Non-dismissable dialog that appears the moment a user with
 * `must_change_password: true` reaches the dashboard. Blocks every route
 * until they set a new password (or sign out).
 */
export function ForcePasswordRotationDialog() {
  const { token, mustChangePassword, refreshMe, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});

  if (!mustChangePassword) return null;

  const validate = () => {
    const e = {};
    if (!current) e.current = 'Enter the temporary password from your admin';
    if (next.length < 8 || !/[A-Za-z]/.test(next) || !/\d/.test(next)) e.next = 'Use at least 8 characters with a letter and a number';
    if (next === current) e.next = 'Pick something different from the temporary password';
    if (confirm !== next) e.confirm = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      await api.changePassword(token, current, next);
      await refreshMe();
      toast.success('Password updated — you’re good to go');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not update password'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* not dismissable */ }}>
      <DialogContent
        hideClose
        className="bg-slate-900 border-slate-800 text-slate-100 sm:max-w-md"
        // Block Radix's default close-on-overlay-click and escape
        onEscapeKeyDown={(e)=>e.preventDefault()}
        onInteractOutside={(e)=>e.preventDefault()}
        onPointerDownOutside={(e)=>e.preventDefault()}
        data-testid="force-password-dialog"
      >
        <DialogHeader>
          <div className="w-11 h-11 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-2">
            <KeyRound className="w-5 h-5 text-blue-300"/>
          </div>
          <DialogTitle className="text-white">Set a new password</DialogTitle>
          <DialogDescription className="text-slate-400">
            Your admin created this account with a temporary password. Choose your own now to unlock the dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="cur-pass" className="text-slate-300 text-xs">Temporary password</Label>
            <Input id="cur-pass" type="password" value={current}
              onChange={(e)=>setCurrent(e.target.value)}
              className="bg-slate-950 border-slate-800 text-white mt-1.5"
              data-testid="cur-pass"/>
            {errors.current && <p className="text-xs text-rose-300 mt-1">{errors.current}</p>}
          </div>
          <div>
            <Label htmlFor="new-pass" className="text-slate-300 text-xs">New password (8+ chars, letter and number)</Label>
            <Input id="new-pass" type="password" value={next}
              onChange={(e)=>setNext(e.target.value)}
              className="bg-slate-950 border-slate-800 text-white mt-1.5"
              data-testid="new-pass"/>
            {errors.next && <p className="text-xs text-rose-300 mt-1">{errors.next}</p>}
          </div>
          <div>
            <Label htmlFor="confirm-pass" className="text-slate-300 text-xs">Confirm new password</Label>
            <Input id="confirm-pass" type="password" value={confirm}
              onChange={(e)=>setConfirm(e.target.value)}
              className="bg-slate-950 border-slate-800 text-white mt-1.5"
              data-testid="confirm-pass"/>
            {errors.confirm && <p className="text-xs text-rose-300 mt-1">{errors.confirm}</p>}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={logout} className="text-slate-400 hover:text-white hover:bg-slate-800" data-testid="force-pass-logout">
            <LogOut className="w-4 h-4 mr-2"/> Sign out instead
          </Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-500 hover:bg-blue-500/90 text-white" data-testid="force-pass-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <KeyRound className="w-4 h-4 mr-2"/>}
            Update password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
